export type TokenProvider = () => Promise<string | null>;

const PRODUCTION_API_BASE = 'https://aetherproxy.spbarkah157.workers.dev';
const LOCAL_API_BASE = 'http://localhost:8787';
const DASHBOARD_PAGES_HOST_SUFFIX = 'aetherproxy-dashboard.pages.dev';

type ApiErrorPayload = {
  error?: {
    message?: unknown;
  };
  message?: unknown;
};

export class AdminApiClient {
  constructor(private baseUrl: string, private getToken: TokenProvider) {}

  setBaseUrl(baseUrl: string) {
    this.baseUrl = trimTrailingSlash(baseUrl);
  }

  async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await this.getToken();
    if (!token) throw new Error('Missing admin token or Clerk session token');

    const headers = new Headers(init.headers);
    headers.set('authorization', `Bearer ${token}`);
    if (init.body && !headers.has('content-type')) {
      headers.set('content-type', 'application/json');
    }

    const response = await fetch(buildApiUrl(this.baseUrl, path), {
      ...init,
      headers,
    });

    return readJsonResponse<T>(response);
  }
}

export async function readJsonResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  const payload = parseResponseBody(text, response.headers.get('content-type'));

  if (!response.ok) {
    throw new Error(errorMessageFromPayload(payload) ?? `HTTP ${response.status}`);
  }

  return payload as T;
}

export function defaultApiBase(): string {
  const configured = import.meta.env.VITE_AETHERPROXY_API_BASE as string | undefined;
  if (configured?.trim()) return normalizeApiBase(configured);

  if (typeof window === 'undefined') return PRODUCTION_API_BASE;

  const { hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return LOCAL_API_BASE;
  }

  return PRODUCTION_API_BASE;
}

export function isDashboardApiBase(value: string): boolean {
  try {
    const url = new URL(value);
    return url.hostname === DASHBOARD_PAGES_HOST_SUFFIX || url.hostname.endsWith(`.${DASHBOARD_PAGES_HOST_SUFFIX}`);
  } catch {
    return false;
  }
}

export function normalizeApiBase(value: string): string {
  const trimmed = trimTrailingSlash(value.trim());
  const url = new URL(trimmed);

  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    throw new Error('API base URL must use http or https.');
  }

  if (url.username || url.password) {
    throw new Error('API base URL must not include credentials.');
  }

  url.hash = '';
  url.search = '';
  return trimTrailingSlash(url.toString());
}

function buildApiUrl(baseUrl: string, path: string): string {
  const normalizedBase = normalizeApiBase(baseUrl);
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
}

function parseResponseBody(text: string, contentType: string | null): unknown {
  if (!text.trim()) return null;

  const trimmed = text.trimStart();
  const isJsonContentType = contentType?.toLowerCase().includes('json') ?? false;
  const looksLikeJson = trimmed.startsWith('{') || trimmed.startsWith('[');

  if (!isJsonContentType && !looksLikeJson) {
    throw new Error(
      `Expected JSON from AetherProxy API but received ${describeContentType(contentType)}. ` +
        `Check the dashboard API base URL. Response preview: ${preview(text)}`,
    );
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new Error(
      `Invalid JSON from AetherProxy API. Check the dashboard API base URL. ` +
        `Response preview: ${preview(text)}`,
    );
  }
}

function errorMessageFromPayload(payload: unknown): string | undefined {
  if (!isRecord(payload)) return undefined;

  const typedPayload: ApiErrorPayload = payload;
  const nestedMessage = typedPayload.error?.message;
  if (typeof nestedMessage === 'string' && nestedMessage.trim()) {
    return nestedMessage;
  }

  const topLevelMessage = typedPayload.message;
  if (typeof topLevelMessage === 'string' && topLevelMessage.trim()) {
    return topLevelMessage;
  }

  return undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function describeContentType(contentType: string | null): string {
  return contentType?.trim() || 'non-JSON response';
}

function preview(text: string): string {
  return text.replace(/\s+/g, ' ').trim().slice(0, 120);
}
